import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { STATUS_KAMAR, statusLabel, ADA_TIDAK, E_KMKB, JENIS_KELAMIN, JAMINAN, KELAS_JAMINAN } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatRm, isValidPersonName, isValidEmail, DPJP_FORMAT_MSG, EMAIL_FORMAT_MSG, NAMA_PASIEN_MAX, NAMA_PASIEN_WARN, namaPasienPanjangMsg } from "@/lib/pengajuanValidation";
import CharCounter from "@/components/CharCounter";
import { fmtDate, fmtDateTime, fmtSelisihWaktu } from "@/lib/format";

const EMPTY = {
  nama_dokter: "", no_hp_dokter: "", email_dokter: "", nama_pasien: "", no_rm: "", espr: "Tidak Ada", e_kmkb: "Belum", los_pasien: "", umur: "",
  jenis_kelamin: "", no_telp: "", ksm_id: "", diagnosa: "", rencana_tindakan: "",
  dpjp_nama: "", jaminan: "", kelas_jaminan: "", tanggal_operasi: "", tanggal_masuk_rawat_inap: "",
  status_kamar: "Menunggu Kamar", kamar_rawat: "", keterangan_batal: "",
};

const Field = ({ label, required, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-medium text-slate-600">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    {children}
  </div>
);

export default function PasienForm({ open, onOpenChange, patient, mode = "full", onSaved }) {
  const isEdit = Boolean(patient);
  const kamarOnly = mode === "kamar";
  const [form, setForm] = useState(EMPTY);
  const [ksmList, setKsmList] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/master/ksm").then((r) => setKsmList(r.data)).catch(() => {});
    if (patient) {
      setForm({
        ...EMPTY, ...patient,
        umur: String(patient.umur ?? ""),
        email_dokter: patient.email_dokter || "",
        e_kmkb: patient.e_kmkb || "Belum",
        los_pasien: patient.los_pasien || "",
        kamar_rawat: patient.kamar_rawat || "",
        keterangan_batal: patient.keterangan_batal || "",
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, patient]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  // Only a new or changed name must fit: an old record's long name stays editable as it is.
  const namaLen = form.nama_pasien.trim().length;
  const namaTerlaluPanjang = namaLen > NAMA_PASIEN_MAX && form.nama_pasien.trim() !== (patient?.nama_pasien || "").trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!kamarOnly && !/^\d{3}-\d{2}-\d{2}$/.test(form.no_rm) && form.no_rm !== patient?.no_rm) {
      toast.error("No. RM harus berformat XXX-XX-XX (contoh: 123-45-67)");
      return;
    }
    if (!kamarOnly && namaTerlaluPanjang) {
      toast.error(namaPasienPanjangMsg(namaLen));
      return;
    }
    if (!kamarOnly && !isValidPersonName(form.dpjp_nama) && form.dpjp_nama !== patient?.dpjp_nama) {
      toast.error(DPJP_FORMAT_MSG);
      return;
    }
    if (!kamarOnly && form.email_dokter.trim() && !isValidEmail(form.email_dokter)) {
      toast.error(EMAIL_FORMAT_MSG);
      return;
    }
    if (!kamarOnly && form.jaminan === "BPJS" && !["1", "2", "3"].includes(form.kelas_jaminan)) {
      toast.error("Kelas Jaminan wajib diisi jika Jaminan BPJS");
      return;
    }
    if (form.status_kamar === "Sudah Dapat Kamar" && !form.kamar_rawat.trim()) {
      toast.error(`Kamar Rawat wajib diisi jika status '${statusLabel("Sudah Dapat Kamar")}'`);
      return;
    }
    if (form.status_kamar === "Batal Operasi" && !form.keterangan_batal.trim()) {
      toast.error(`Keterangan Batal wajib diisi jika status '${statusLabel("Batal Operasi")}'`);
      return;
    }
    setSaving(true);
    try {
      if (kamarOnly) {
        await api.put(`/patients/${patient.id}/kamar`, {
          status_kamar: form.status_kamar,
          kamar_rawat: form.kamar_rawat || null,
          keterangan_batal: form.keterangan_batal || null,
        });
        toast.success("Status kamar berhasil diperbarui");
      } else if (isEdit) {
        await api.put(`/patients/${patient.id}`, { ...form, umur: parseInt(form.umur, 10) });
        toast.success("Data pasien berhasil diperbarui");
      } else {
        await api.post("/patients", { ...form, umur: parseInt(form.umur, 10) });
        toast.success("Pasien berhasil ditambahkan");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(apiError(err));
      // Someone else already filled the room status (it is now locked): drop the stale row from the queue.
      if (kamarOnly && err?.response?.status === 409) {
        onOpenChange(false);
        onSaved?.();
      }
    } finally {
      setSaving(false);
    }
  };

  const selectCls = "bg-white";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto bg-white" data-testid="pasien-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">
            {kamarOnly ? "Update Status Kamar" : isEdit ? "Edit Data Pasien" : "Tambah Pasien Baru"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {kamarOnly
              ? `${patient?.nama_pasien} — ${patient?.no_rm}. Hanya field terkait kamar yang dapat diubah.`
              : "Field bertanda * wajib diisi. Departemen dipilih dari master data; DPJP diketik langsung."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-2">
          {!kamarOnly && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Nama Dokter Pengaju" required>
                  <Input value={form.nama_dokter} onChange={(e) => set("nama_dokter", e.target.value)} required data-testid="form-nama-dokter" />
                </Field>
                <Field label="No. HP Dokter Pengaju" required>
                  <Input type="tel" value={form.no_hp_dokter} onChange={(e) => set("no_hp_dokter", e.target.value)} required data-testid="form-no-hp-dokter" />
                </Field>
                <Field label="Email Dokter Pengaju">
                  <Input type="email" value={form.email_dokter} onChange={(e) => set("email_dokter", e.target.value)} maxLength={254}
                    placeholder="nama@domain.com" data-testid="form-email-dokter" />
                </Field>
                <Field label="Nama Pasien" required>
                  <Input value={form.nama_pasien} onChange={(e) => set("nama_pasien", e.target.value)} required data-testid="form-nama-pasien" />
                  <CharCounter length={namaLen} max={NAMA_PASIEN_MAX} warnAt={NAMA_PASIEN_WARN} testId="form-nama-pasien-counter" />
                  {namaTerlaluPanjang && <p className="text-xs text-red-600" data-testid="form-nama-pasien-error">{namaPasienPanjangMsg(namaLen)}</p>}
                </Field>
                <Field label="No. RM" required>
                  <Input value={form.no_rm} onChange={(e) => set("no_rm", formatRm(e.target.value))} required inputMode="numeric"
                    placeholder="123-45-67" maxLength={9} title="Format: XXX-XX-XX (contoh: 123-45-67)"
                    className="font-mono" data-testid="form-no-rm" />
                </Field>
                <Field label="Umur" required>
                  <Input type="number" min="0" max="150" value={form.umur} onChange={(e) => set("umur", e.target.value)} required data-testid="form-umur" />
                </Field>
                <Field label="Jenis Kelamin" required>
                  <Select value={form.jenis_kelamin} onValueChange={(v) => set("jenis_kelamin", v)} required>
                    <SelectTrigger data-testid="form-jenis-kelamin"><SelectValue placeholder="Pilih jenis kelamin" /></SelectTrigger>
                    <SelectContent className={selectCls}>
                      {JENIS_KELAMIN.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="No. Telp yang bisa dihubungi" required>
                  <Input value={form.no_telp} onChange={(e) => set("no_telp", e.target.value)} required data-testid="form-no-telp" />
                </Field>
                <Field label="E-SPR" required>
                  <Select value={form.espr} onValueChange={(v) => set("espr", v)}>
                    <SelectTrigger data-testid="form-espr"><SelectValue /></SelectTrigger>
                    <SelectContent className={selectCls}>
                      {ADA_TIDAK.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="E-KMKB" required>
                  <Select value={form.e_kmkb} onValueChange={(v) => set("e_kmkb", v)}>
                    <SelectTrigger data-testid="form-e-kmkb"><SelectValue /></SelectTrigger>
                    <SelectContent className={selectCls}>
                      {E_KMKB.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <div className="sm:col-span-2">
                  <Field label="LOS Pasien">
                    <Input value={form.los_pasien} maxLength={200} placeholder="Contoh: 3 hari / 5 hari (isi lama rawat saja, bukan catatan klinis)"
                      onChange={(e) => set("los_pasien", e.target.value)} data-testid="form-los" />
                  </Field>
                </div>
                <Field label="Departemen" required>
                  <Select
                    value={form.ksm_id}
                    onValueChange={(v) => set("ksm_id", v)}
                  >
                    <SelectTrigger data-testid="form-ksm"><SelectValue placeholder="Pilih Departemen" /></SelectTrigger>
                    <SelectContent className={selectCls}>
                      {ksmList.map((k) => <SelectItem key={k.id} value={k.id}>{k.nama}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="DPJP" required>
                  <Input value={form.dpjp_nama} onChange={(e) => set("dpjp_nama", e.target.value)} required maxLength={100}
                    placeholder="Ketik nama DPJP (mis. dr. Andi, Sp.B)" autoComplete="off" data-testid="form-dpjp" />
                </Field>
                <Field label="Jaminan" required>
                  <Select value={form.jaminan} onValueChange={(v) => {
                    set("jaminan", v);
                    if (v !== "BPJS") set("kelas_jaminan", "");
                  }}>
                    <SelectTrigger data-testid="form-jaminan"><SelectValue placeholder="Pilih jaminan" /></SelectTrigger>
                    <SelectContent className={selectCls}>
                      {JAMINAN.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                {form.jaminan === "BPJS" && (
                  <Field label="Kelas Jaminan" required>
                    <Select value={form.kelas_jaminan} onValueChange={(v) => set("kelas_jaminan", v)}>
                      <SelectTrigger data-testid="form-kelas-jaminan"><SelectValue placeholder="Pilih kelas" /></SelectTrigger>
                      <SelectContent className={selectCls}>
                        {KELAS_JAMINAN.map((k) => <SelectItem key={k} value={k}>Kelas {k}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                )}
                <Field label="Tanggal Jadwal Operasi/Tindakan" required>
                  <Input type="date" value={form.tanggal_operasi} onChange={(e) => set("tanggal_operasi", e.target.value)} required data-testid="form-tanggal-operasi" />
                </Field>
                <Field label="Tanggal Masuk Rawat Inap">
                  <Input type="date" value={form.tanggal_masuk_rawat_inap} onChange={(e) => set("tanggal_masuk_rawat_inap", e.target.value)} data-testid="form-tanggal-masuk-ranap" />
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Diagnosa">
                  <Textarea rows={3} value={form.diagnosa} onChange={(e) => set("diagnosa", e.target.value)} data-testid="form-diagnosa" />
                </Field>
                <Field label="Rencana Tindakan">
                  <Textarea rows={3} value={form.rencana_tindakan} onChange={(e) => set("rencana_tindakan", e.target.value)} data-testid="form-rencana-tindakan" />
                </Field>
              </div>
              <div className="border-t border-slate-200 pt-4">
                <p className="text-sm font-semibold text-slate-800 mb-3">Status Kamar</p>
              </div>
            </>
          )}

          {kamarOnly && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs" data-testid="kamar-info-panel">
              <div>
                <span className="text-slate-500">Waktu Pengajuan: </span>
                <span className="font-medium text-slate-700">{fmtDateTime(patient?.created_at)}</span>
              </div>
              <div>
                <span className="text-slate-500">Pasien Datang: </span>
                <span className="font-medium text-slate-700">{patient?.pasien_datang_at ? fmtDateTime(patient.pasien_datang_at) : "Belum ditandai"}</span>
              </div>
              <div>
                <span className="text-slate-500">Tanggal Masuk Rawat Inap (rencana dokter): </span>
                <span className="font-medium text-slate-700">
                  {patient?.tanggal_masuk_rawat_inap ? fmtDate(patient.tanggal_masuk_rawat_inap) : "-"}
                </span>
              </div>
              {patient?.waktu_dapat_kamar && (
                <>
                  <div>
                    <span className="text-slate-500">Waktu Dapat Kamar: </span>
                    <span className="font-medium text-slate-700">{fmtDateTime(patient.waktu_dapat_kamar)}</span>
                  </div>
                  {patient.pasien_datang_at && (
                    <div>
                      <span className="text-slate-500">Durasi Menunggu (datang → dapat kamar): </span>
                      <span className="font-medium text-[#009E94]">{fmtSelisihWaktu(patient.pasien_datang_at, patient.waktu_dapat_kamar)}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-slate-500">Lead Time Pengajuan (pengajuan → dapat kamar): </span>
                    <span className="font-medium text-slate-700">{fmtSelisihWaktu(patient.created_at, patient.waktu_dapat_kamar)}</span>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Status Kamar" required>
              <Select value={form.status_kamar} onValueChange={(v) => set("status_kamar", v)}>
                <SelectTrigger data-testid="form-status-kamar"><SelectValue /></SelectTrigger>
                <SelectContent className={selectCls}>
                  {STATUS_KAMAR.map((s) => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            {form.status_kamar === "Sudah Dapat Kamar" && (
              <Field label="Kamar Rawat" required>
                <Input value={form.kamar_rawat} onChange={(e) => set("kamar_rawat", e.target.value)} placeholder="mis. A - 302B" data-testid="form-kamar-rawat" />
              </Field>
            )}
            {form.status_kamar === "Batal Operasi" && (
              <div className="sm:col-span-2">
                <Field label={`Keterangan ${statusLabel("Batal Operasi")}`} required>
                  <Textarea rows={2} value={form.keterangan_batal} onChange={(e) => set("keterangan_batal", e.target.value)} data-testid="form-keterangan-batal" />
                </Field>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="form-cancel-button">
              Batal
            </Button>
            <Button type="submit" disabled={saving} data-testid="form-submit-button"
              className="bg-[#00B9AD] hover:bg-[#009E94] text-white">
              {saving ? "Menyimpan..." : isEdit || kamarOnly ? "Simpan Perubahan" : "Tambah Pasien"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
